import React, { useState } from 'react';
import { useGetCompetitorReportQuery, PlatformIntentKeyword } from 'store/services/admin/adminSeoApi';
import './CompetitorIntelTab.css';

const getPositionClass = (position: number): string => {
  if (position <= 10) return 'position-green';
  if (position <= 15) return 'position-yellow';
  return 'position-orange';
};

const getThreatClass = (level: string): string => {
  switch (level) {
    case 'High': return 'threat-high';
    case 'Medium': return 'threat-medium';
    case 'Low': return 'threat-low';
    default: return '';
  }
};

interface Props {
  isActive: boolean;
}

const CompetitorIntelTab: React.FC<Props> = ({ isActive }) => {
  const { data, isLoading, error } = useGetCompetitorReportQuery(undefined, { skip: !isActive });
  const [expandedKeyword, setExpandedKeyword] = useState<string | null>(null);

  if (isLoading) {
    return <div className="seo-loading">Loading competitor data...</div>;
  }

  if (error) {
    return (
      <div className="seo-error-state">
        <h3>Failed to load competitor report</h3>
        <p>Please try again later.</p>
      </div>
    );
  }

  const isReport = data && !('status' in data);

  if (!isReport) {
    return (
      <div className="seo-empty-state">
        <h3>No competitor analysis has been generated yet.</h3>
        <p>Click &quot;Full Analysis&quot; to analyze competitors for your gap zone keywords.</p>
      </div>
    );
  }

  const toggleKeyword = (query: string) => {
    setExpandedKeyword(prev => prev === query ? null : query);
  };

  return (
    <>
      {/* Summary Cards */}
      <div className="seo-stats seo-stats-3">
        <div className="seo-stat-card">
          <span className="seo-stat-label">Platform-Intent Keywords</span>
          <span className="seo-stat-value">{data.summary.totalPlatformIntentKeywords}</span>
        </div>
        <div className="seo-stat-card">
          <span className="seo-stat-label">High Threat Domains</span>
          <div className="ci-threat-domains">
            {data.summary.highThreatDomains.length > 0 ? (
              data.summary.highThreatDomains.map(domain => (
                <span key={domain} className="ci-domain-tag threat-high">{domain}</span>
              ))
            ) : (
              <span className="ci-no-threats">None</span>
            )}
          </div>
        </div>
        <div className="seo-stat-card">
          <span className="seo-stat-label">Top Opportunity</span>
          <span className="seo-stat-value seo-stat-value-sm">{data.summary.topOpportunity || 'N/A'}</span>
        </div>
      </div>

      {/* Keyword Accordion */}
      <div className="ci-keyword-list">
        {data.platformIntentKeywords.map((kw: PlatformIntentKeyword) => (
          <div
            key={kw.query}
            className={`ci-keyword-card ${expandedKeyword === kw.query ? 'ci-expanded' : ''}`}
          >
            <div className="ci-keyword-row" onClick={() => toggleKeyword(kw.query)}>
              <span className={`ci-chevron ${expandedKeyword === kw.query ? 'ci-chevron-open' : ''}`}>
                &#9654;
              </span>
              <span className="ci-keyword-name">{kw.query}</span>
              <span className={`seo-position-badge ${getPositionClass(kw.position)}`}>
                {kw.position.toFixed(1)}
              </span>
              <span className="ci-impressions">{kw.impressions.toLocaleString()} imp</span>
              <span className={`ci-confidence-badge ci-confidence-${kw.intentConfidence}`}>
                {kw.intentConfidence}
              </span>
            </div>

            {expandedKeyword === kw.query && (
              <div className="ci-competitor-table">
                <div className="ci-comp-header">
                  <span>#</span>
                  <span>Domain</span>
                  <span>Content Type</span>
                  <span>Threat</span>
                  <span>Why Winning</span>
                  <span>Suggested Fix</span>
                </div>
                {kw.competitors.map((comp, idx) => (
                  <div className="ci-comp-row" key={idx}>
                    <span className="ci-comp-pos">{comp.estimatedPosition}</span>
                    <span className="ci-comp-domain">
                      <a href={comp.url} target="_blank" rel="noopener noreferrer">{comp.domain}</a>
                    </span>
                    <span className="ci-comp-type">{comp.contentType}</span>
                    <span>
                      <span className={`ci-threat-badge ${getThreatClass(comp.threatLevel)}`}>
                        {comp.threatLevel}
                      </span>
                    </span>
                    <span className="ci-comp-text">{comp.whyWinning}</span>
                    <span className="ci-comp-text">{comp.specificFix}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default CompetitorIntelTab;
